import { Injectable } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { CONSTANTS } from '../../Constants/Constants';
import { CollaborationState, EscalationData, EstimateData, Escalations, EstimateGrossMargin, EstimateTab, Subcontractors } from '../../models/dtos';
import { SellPriceConfig, SellPriceCalculationResult } from '../../models/sol-estimate-table/SellPriceConfig';
import { DynamicFeeCostInputs } from '../../models/sol-estimate-table/DynamicFeeCostInputs';
import {
  EstimateRowModel,
  EstimateRowExtendedModel,
  EstimateUiModel,
} from '../../models/sol-estimate-table/ui-models/index';
import { roundHalfEven9 } from '../../../types/number-extensions';
import { MaterialSubCategory, SolMaterialEscalationType, TaxTypes } from '../../models/enum';
import { CommonDataService } from '../common-data.service';
import { TurboGMResult } from '../../models/sol-estimate-table/TurboGMResult';
import { CommonFunctionsService } from '../common-functions.service';
import { EstimateRowExtendedModelSubContractor } from '../../models/sol-estimate-table/ui-models/estimate-row.model';

/** Any row that carries a cost and a % of TDC. */
type TdcRow = { cost: number; tdcPercentage: number };

/**
 * Shared utility service for the estimate tab.
 *
 * Consolidates:
 *  • Sell Price calculations (calculateRowSellPrice, resolveRowGMPercent, etc.)
 *  • Model update helpers (updateRowResult, updateSectionTotalsHeader, etc.)
 *  • Format helpers (formatCurrency, formatPercent, parseCurrencyString)
 *  • Named row lookup on models
 *
 * This service is stateless and works ONLY with plain UI models.
 * No FormGroup references.
 */
@Injectable({
  providedIn: 'root'
})
export class EstimateCommonFunctionsService {

  private readonly MECHANICAL_FEE_BUSINESS_UNIT_ID = 14;
  private readonly MECHANICAL_FEE_ZWEIG_IDS: readonly number[] = [50, 51];
  private readonly MECHANICAL_FEE_SUB_SEGMENT_ID = 8;

  public estimateNonTaxableItemsDictionary: Map<string, {
    referenceNumber: string;
    cost: number;
    sellPrice: number;
  }> = new Map();

  constructor(
    private readonly currencyPipe: CurrencyPipe,
    private readonly commonDataService: CommonDataService,
    private readonly commonFunctionsService: CommonFunctionsService,
  ) { }

  resolveRowGMPercent(config: SellPriceConfig, presentGMPercent?: number): number {
    // Fixed Margin/Price: soldAsGMPercent is already mode-appropriate (MU% or GM%)
    if (config.isFixedPriceSelected !== null) {
      return config.soldAsGMPercent;
    }
    // Line Item: row.grossMarginPercentage is always true GM%, convert to MU% if needed
    if (presentGMPercent !== undefined) {
      return config.isMarkUpSelected ? this.getMarkUpPercent(presentGMPercent) : presentGMPercent;
    }
    return config.soldAsGMPercent;
  }

  /**
   * Expands a single percent value (already in the active GM/MU unit) into both units.
   */
  resolvePercentPair(percent: number, isMarkUp: boolean): { grossMarginPercentage: number; markUpPercentage: number } {
    const value = Number.isFinite(percent) ? percent : CONSTANTS.ZERO;
    return isMarkUp
      ? { grossMarginPercentage: this.getGrossMarginPercent(value), markUpPercentage: value }
      : { grossMarginPercentage: value, markUpPercentage: this.getMarkUpPercent(value) };
  }

  /**
   * Sell price a zero-cost row must keep, or undefined when the margin should be kept instead.
   * Only Line Item rows track which field drove the last edit.
   */
  resolvePreservedSellPrice(
    row: EstimateRowModel | EstimateRowExtendedModel,
    config: SellPriceConfig,
    isSellPriceLastModified: boolean
  ): number | undefined {
    return (config.isFixedPriceSelected === null && isSellPriceLastModified) ? row.sellPrice : undefined;
  }

  calculateRowSellPrice(
    cost: number,
    percent: number,
    isMarkUp: boolean,
    preservedSellPrice?: number
  ): SellPriceCalculationResult {
    if (cost === CONSTANTS.ZERO) {
      // Nothing is derivable from a zero cost, so only the user's last edited field survives.
      if (preservedSellPrice !== undefined) {
        return {
          sellPrice: preservedSellPrice,
          gmDollars: CONSTANTS.ZERO,
          grossMarginPercentage: CONSTANTS.ZERO,
          markUpPercentage: CONSTANTS.ZERO
        };
      }
      const { grossMarginPercentage, markUpPercentage } = this.resolvePercentPair(percent, isMarkUp);
      return {
        sellPrice: CONSTANTS.ZERO,
        gmDollars: CONSTANTS.ZERO,
        grossMarginPercentage,
        markUpPercentage
      };
    }

    let sellPrice: number;
    if (isMarkUp) {
      sellPrice = roundHalfEven9(cost * (1 + percent / CONSTANTS.HUNDRED));
    } else {
      sellPrice = roundHalfEven9(cost / (1 - percent / CONSTANTS.HUNDRED));
    }

    return this.recomputePercentsFromSellPrice(cost, sellPrice);
  }

  updateSectionTotalsHeader(
    headerRow: EstimateRowModel | EstimateRowExtendedModel,
    totalCost: number,
    totalSellPrice: number,
    subTotalsCost: number,
    config: SellPriceConfig
  ): void {
    headerRow.cost = totalCost;
    let result: SellPriceCalculationResult;
    if (totalCost !== CONSTANTS.ZERO) {
      result = this.recomputePercentsFromSellPrice(totalCost, totalSellPrice);
    } else if (totalSellPrice === CONSTANTS.ZERO && config.isFixedPriceSelected === null) {
      result = { sellPrice: CONSTANTS.ZERO, gmDollars: CONSTANTS.ZERO, grossMarginPercentage: CONSTANTS.ZERO, markUpPercentage: CONSTANTS.ZERO };
    } else {
      result = this.zeroCostHeaderResult(totalSellPrice, config, headerRow);
    }
    this.updateRowResult(headerRow, result, subTotalsCost);
  }

  private zeroCostHeaderResult(sellPrice: number, config: SellPriceConfig, headerRow: EstimateRowModel | EstimateRowExtendedModel): SellPriceCalculationResult {
    const pair = config.isFixedPriceSelected !== null
      ? this.resolvePercentPair(config.soldAsGMPercent, config.isMarkUpSelected)
      : (headerRow.sellPrice !== CONSTANTS.ZERO ?
        { grossMarginPercentage: CONSTANTS.ZERO, markUpPercentage: CONSTANTS.ZERO } :
        this.resolvePercentPair(config.soldAsGMPercent, config.isMarkUpSelected)
      );
    // Zero cost has no margin; the stale headerRow.sellPrice kept the old GM$ visible after hiding.
    return { sellPrice, gmDollars: CONSTANTS.ZERO, ...pair };
  }

  /**
   * Updates a single row model with computed result fields.
   */
  updateRowResult(
    row: EstimateRowModel | EstimateRowExtendedModel | EstimateRowExtendedModelSubContractor,
    result: SellPriceCalculationResult,
    subTotalsCost: number = CONSTANTS.ZERO
  ): void {
    row.sellPrice = result.sellPrice;
    row.grossMargin = result.gmDollars;
    row.grossMarginPercentage = result.grossMarginPercentage;
    row.markUpPercentage = result.markUpPercentage;
    row.tdcPercentage = this.calculateTdcPercent(row.cost, subTotalsCost);
  }

  /**
   * Updates a standalone row model with cost and derived sell price fields.
   */
  updateStandaloneRowWithSellPrice(
    row: EstimateRowModel,
    cost: number,
    config: SellPriceConfig,
    subTotalsCost: number
  ): void {
    const presentGMPercent = row.grossMarginPercentage;
    const gmPercent = this.resolveRowGMPercent(config, Number.isNaN(presentGMPercent) ? undefined : presentGMPercent);
    const result = this.calculateRowSellPrice(cost, gmPercent, config.isMarkUpSelected);
    row.cost = cost;
    this.updateRowResult(row, result, subTotalsCost);
  }

  calculateTurboGM(
    subTotalsCost: number,
    soldAsSellPrice: number,
    materialTaxSellPrice: number,
    laborTaxSellPrice: number,
    isTaxTypeBoOrSales: boolean,
    isMarkUpSelected: boolean,
    soldAsGMPercent: number
  ): TurboGMResult {
    const costPrice = Math.round(subTotalsCost * 100) / 100; // CALCULATIONS_PRECISIONS = 2 (adjust as needed)

    if (costPrice === 0 || soldAsSellPrice === 0) {
      return this.calculateTurboGMFromSoldAsGM(soldAsGMPercent, isMarkUpSelected);
    }

    const sellPriceForTurboGM = soldAsSellPrice - (isTaxTypeBoOrSales ? (materialTaxSellPrice + laborTaxSellPrice) : 0);

    let turboGMPercent = 1 - (subTotalsCost / sellPriceForTurboGM);
    let reportedGMPercent: number;

    if (isMarkUpSelected) {
      reportedGMPercent = turboGMPercent === 1 ? 0 : turboGMPercent / (1 - turboGMPercent);
    } else {
      reportedGMPercent = turboGMPercent;
    }

    turboGMPercent = turboGMPercent * 100;
    reportedGMPercent = reportedGMPercent * 100;

    return {
      soldAsTurboGMPercent: turboGMPercent,
      soldAsReportedGMPercent: reportedGMPercent,
    };
  }

  calculateTurboGMFromSoldAsGM(soldAsGMPercent: number, isMarkUpSelected: boolean): TurboGMResult {
    const reportedGMPercent = soldAsGMPercent;
    let turboGMPercent: number;

    if (isMarkUpSelected) {
      if (soldAsGMPercent === -100) {
        turboGMPercent = 0;
      } else {
        turboGMPercent = (soldAsGMPercent / (100 + soldAsGMPercent)) * 100;
      }
    } else {
      turboGMPercent = soldAsGMPercent;
    }

    return {
      soldAsTurboGMPercent: turboGMPercent,
      soldAsReportedGMPercent: reportedGMPercent,
    };
  }

  calculateOpportunitySellPrice(data: EstimateData): number {
    const estimateTab = data.projectObject?.metaData?.estimateMetaData?.estimateTab;
    const estimateSettings = data.projectObject?.metaData?.estimateMetaData?.estimateSettings;

    const taxTypeID = data.taxTypeID ?? TaxTypes.NoTax;
    const isSellPriceWithTax = estimateSettings?.isSellPriceWithTax ?? true;

    const isOpportunityWithoutSalesTax = (taxTypeID === TaxTypes.SalesTax) && !isSellPriceWithTax;

    if (isOpportunityWithoutSalesTax) {
      return estimateTab?.subTotalSellprice ?? CONSTANTS.ZERO;
    }
    return estimateTab?.soldASSellPrice ?? CONSTANTS.ZERO;
  }

  /** MU% -> GM% at 9 dp (matches desktop decimal scale); display rounding belongs to the UI pipes. */
  getGrossMarginPercent(markUpPercent: number): number {
    const denominator = CONSTANTS.HUNDRED + markUpPercent;
    return denominator === CONSTANTS.ZERO ? CONSTANTS.ZERO : roundHalfEven9((markUpPercent / denominator) * CONSTANTS.HUNDRED);
  }

  /** GM% -> MU% at 9 dp (matches desktop decimal scale); display rounding belongs to the UI pipes. */
  getMarkUpPercent(gmPercent: number): number {
    const denominator = CONSTANTS.HUNDRED - gmPercent;
    return denominator === CONSTANTS.ZERO ? CONSTANTS.ZERO : roundHalfEven9((gmPercent / denominator) * CONSTANTS.HUNDRED);
  }

  calculateTdcPercent(rowCost: number, subTotalsCost: number): number {
    return subTotalsCost === CONSTANTS.ZERO ? CONSTANTS.ZERO : roundHalfEven9((rowCost / subTotalsCost) * CONSTANTS.HUNDRED);
  }

  /**
   * Restates every row's % of TDC against the given sub-total cost.
   * Rows compute their TDC% while the sub-total is still the previous one,
   * so this runs once the new sub-total is known.
   */
  refreshTdcPercentages(model: EstimateUiModel, subTotalsCost: number): void {
    const apply = (row: TdcRow | null | undefined): void => {
      if (row) {
        row.tdcPercentage = this.calculateTdcPercent(row.cost, subTotalsCost);
      }
    };
    const applyAll = (rows: TdcRow[] | null | undefined): void => {
      for (const row of rows ?? []) {
        apply(row);
      }
    };

    const mat = model.materialCosts;
    apply(mat.materialCostsHeader);
    for (const category of [mat.siemensMaterial, mat.outsideMaterial, mat.mechanicalMaterial, mat.electricalMaterial]) {
      apply(category.headerRow);
      apply(category.shippingAndHandling);
      apply(category.warranty);
    }

    applyAll(Object.values(model.laborCosts));

    apply(model.subContractorCosts.headerRow);
    applyAll(model.subContractorCosts.subContractorsCostsRow);

    const misc = model.miscRiskTravelCosts;
    apply(misc.headerRow);
    applyAll(misc.rows);
    if (misc.riskCosts) {
      apply(misc.riskCosts.headerRow);
      applyAll(misc.riskCosts.rows);
    }

    apply(model.taxNotIncluded);
    for (const taxRate of [model.taxMaterialRate, model.taxMaterialLaborRate]) {
      apply(taxRate.headerRow);
      applyAll(taxRate.taxRateRows);
    }

    apply(model.technologyFee);
  }

  recomputePercentsFromSellPrice(cost: number, sellPrice: number): SellPriceCalculationResult {
    const gmDollars = roundHalfEven9(sellPrice - cost);
    const grossMarginPercentage = sellPrice === CONSTANTS.ZERO ?
      CONSTANTS.ZERO :
      (cost == CONSTANTS.ZERO ? CONSTANTS.ZERO : roundHalfEven9(((sellPrice - cost) / sellPrice) * CONSTANTS.HUNDRED));
    const markUpPercentage = cost === CONSTANTS.ZERO ? CONSTANTS.ZERO : roundHalfEven9(((sellPrice - cost) / cost) * CONSTANTS.HUNDRED);
    return { sellPrice, gmDollars, grossMarginPercentage, markUpPercentage };
  }

  refreshRowPercentsFromSellPrice(row: EstimateRowModel): void {
    const cost = row.cost;
    if (cost === CONSTANTS.ZERO) {
      // Zero-cost percentages are owned by the calculation pass, not derivable here.
      return;
    }
    const result = this.recomputePercentsFromSellPrice(cost, row.sellPrice);
    row.grossMargin = result.gmDollars;
    row.grossMarginPercentage = result.grossMarginPercentage;
    row.markUpPercentage = result.markUpPercentage;
  }

  findNamedRowInModel(
    miscRows: EstimateRowExtendedModel[],
    riskRows: EstimateRowExtendedModel[] | null,
    rowName: string
  ): EstimateRowExtendedModel | null {
    for (const row of miscRows) {
      if (row.name === rowName) return row;
    }
    if (riskRows) {
      for (const row of riskRows) {
        if (row.name === rowName) return row;
      }
    }
    return null;
  }

  isUserDefinedSubContractor(sub: Subcontractors): boolean {
    const referenceNumber = sub.referenceNumber ?? CONSTANTS.EMPTY_STRING;
    return referenceNumber.startsWith(CONSTANTS.USER_DEFINED_SUBCONTRACTOR_PREFIX)
      || referenceNumber === CONSTANTS.USER_DEFINED_SUBCONTRACTOR_REFERENCE;
  }

  /** All user defined subcontractors share one key, so they collapse into a single estimate row. */
  getSubContractorKey(sub: Subcontractors): string {
    if (this.isUserDefinedSubContractor(sub)) {
      return CONSTANTS.USER_DEFINED_SUBCONTRACTOR_REFERENCE;
    }
    return sub.referenceNumber ?? sub.description ?? CONSTANTS.EMPTY_STRING;
  }

  /**
   * Calculates the subcontractor fee cost.
   */
  calculateSubcontractorFeeCost(
    subcontractorTotalCost: number,
    useSubFee: boolean,
    subcontractorFeePercentage: number,
    subcontractorFeeThresholdCost: number
  ): number {
    const isSubcontractorFeeApplicable = this.commonFunctionsService.isSdiApplicable()
      && useSubFee;

    if (!isSubcontractorFeeApplicable || subcontractorTotalCost < subcontractorFeeThresholdCost) {
      return CONSTANTS.ZERO;
    }
    return roundHalfEven9(subcontractorTotalCost * (subcontractorFeePercentage / CONSTANTS.HUNDRED));
  }

  /**
   * Base the auto-risk percentage is applied to. Zero when auto-risk is off or in PPM mode.
   * Tax is added only for cost-type taxes (UseTax / HST / PST).
   */
  calculateTdcForRisk(inputs: DynamicFeeCostInputs, useAutoRisk: boolean): number {
    if (!useAutoRisk || this.commonDataService.globalPPMmode) {
      return CONSTANTS.ZERO;
    }
    // Desktop LaborSpecialitiesCostTotal: 0 in non-PPM, AdditionalLaborCategoryVM.LaborCategoryCostTotal in PPM (not added here; PPM returns above).
    const totalTDC = roundHalfEven9(
      inputs.miscCost +
      inputs.travelCost +
      inputs.subcontractorFeeCost +
      inputs.productivityReserveCost +
      inputs.materialCost +
      inputs.laborCost +
      inputs.subcontractorCost
    );
    const taxTypeID = this.commonDataService.getEstimateMetaData()?.estimateSettings?.taxTypeID;
    const isCostTax = taxTypeID === TaxTypes.UseTax || taxTypeID === TaxTypes.HST || taxTypeID === TaxTypes.PST;
    return isCostTax ? roundHalfEven9(totalTDC + roundHalfEven9(inputs.taxCost ?? CONSTANTS.ZERO)) : totalTDC;
  }

  /**
   * Calculates the auto-risk (calculated risk) cost.
   */
  calculateAutoRiskCost(
    inputs: DynamicFeeCostInputs,
    useAutoRisk: boolean,
    autoRiskPercentage: number
  ): number {
    const tdcForRisk = this.calculateTdcForRisk(inputs, useAutoRisk);
    return roundHalfEven9(tdcForRisk * (autoRiskPercentage / CONSTANTS.HUNDRED));
  }

  /**
   * Calculates the mechanical fee cost.
   */
  calculateMechanicalFeeCost(
    inputs: DynamicFeeCostInputs,
    autoRiskCost: number,
    useMechFee: boolean,
    mechanicalFeePercentage: number
  ): number {
    if (!useMechFee) {
      return CONSTANTS.ZERO;
    }
    const baseCost =
      inputs.materialCost +
      inputs.laborCost +
      inputs.miscCost +
      inputs.subcontractorCost +
      inputs.travelCost +
      inputs.subcontractorFeeCost +
      inputs.productivityReserveCost +
      autoRiskCost;

    return roundHalfEven9(baseCost * (mechanicalFeePercentage / CONSTANTS.HUNDRED));
  }

  /** True only for the Mechanical business estimates that can carry a Mechanical Fee. */
  isMechanicalFeeApplicable(): boolean {
    const estimateData = this.commonDataService.getEstimateData();
    const estimateSettings = this.commonDataService.getEstimateMetaData()?.estimateSettings;
    const businessUnitId = estimateSettings?.businessUnitId ?? estimateData?.businessUnitID;
    const zweigId = estimateData?.profitCenterId;
    const subSegmentId = estimateSettings?.subSegmentId ?? estimateData?.subSegmentID;

    return businessUnitId === this.MECHANICAL_FEE_BUSINESS_UNIT_ID &&
      this.MECHANICAL_FEE_ZWEIG_IDS.includes(zweigId) &&
      subSegmentId === this.MECHANICAL_FEE_SUB_SEGMENT_ID;
  }

  // ═══════════════════════════════════════════════════════════════
  // ── Shipping & Handling ────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════

  /**
   * Calculates the shipping & handling cost for a material category.
   * Formula: dollars * (rate / 100)
   * @param dollars - The base material cost (or sell price in PPM mode)
   * @param rate    - The shipping rate percentage from EstimateGrossMargin
   */
  calculateShippingCost(dollars: number, rate: number): number {
    return roundHalfEven9(dollars * (rate / CONSTANTS.HUNDRED));
  }

  /**
   * Reverse-calculates the shipping rate from a known total and dollar base.
   * Formula: (total / dollars) * 100
   */
  calculateShippingRateFromTotal(total: number, dollars: number): number {
    if (dollars === CONSTANTS.ZERO) { return CONSTANTS.ZERO; }
    return roundHalfEven9((total / dollars) * CONSTANTS.HUNDRED);
  }

  // ═══════════════════════════════════════════════════════════════
  // ── Warranty ───────────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════

  /**
   * Calculates the total warranty cost using all 5 year rates.
   * Formula: dollars * ((yr1 + yr2 + yr3 + yr4 + yr5) / 100)
   * @param dollars   - The base material cost
   * @param year1Rate - Year 1 warranty rate %
   * @param year2Rate - Year 2 warranty rate %
   * @param year3Rate - Year 3 warranty rate %
   * @param year4Rate - Year 4 warranty rate %
   * @param year5Rate - Year 5 warranty rate %
   */
  calculateWarrantyTotal(
    dollars: number,
    year1Rate: number,
    year2Rate: number,
    year3Rate: number,
    year4Rate: number,
    year5Rate: number
  ): number {
    const totalRate = year1Rate + year2Rate + year3Rate + year4Rate + year5Rate;
    return roundHalfEven9(dollars * (totalRate / CONSTANTS.HUNDRED));
  }

  /**
   * Reverse-calculates year-1 warranty rate from a given total (years 2–5 assumed zero).
   * Formula: (total / dollars) * 100
   */
  calculateWarrantyYear1RateFromTotal(total: number, dollars: number): number {
    if (dollars === CONSTANTS.ZERO) { return CONSTANTS.ZERO; }
    return roundHalfEven9((total / dollars) * CONSTANTS.HUNDRED);
  }

  // ═══════════════════════════════════════════════════════════════
  // ── Shipping & Warranty by Category ────────────────────────────
  // ═══════════════════════════════════════════════════════════════

  /**
   * Returns the shipping rate for a material category from EstimateGrossMargin.
   * @param gm - The EstimateGrossMargin data
   * @param category - Which material type ('siemens' | 'outside' | 'mechanical' | 'electrical')
   */
  getShippingRateForCategory(
    gm: EstimateGrossMargin,
    category: MaterialSubCategory
  ): number {
    switch (category) {
      case MaterialSubCategory.Siemens: return gm.siemensMaterialShipping;
      case MaterialSubCategory.Outside: return gm.outsideMaterialShipping;
      case MaterialSubCategory.Mechanical: return gm.machanicalMaterialShipping;
      case MaterialSubCategory.Electrical: return gm.electricalMaterialShipping;
      default: return CONSTANTS.ZERO;
    }
  }

  /**
   * Returns the warranty rates (5 years) for a material category from EstimateGrossMargin.
   * @param gm - The EstimateGrossMargin data
   * @param category - Which material type ('siemens' | 'outside' | 'mechanical' | 'electrical')
   */
  getWarrantyRatesForCategory(
    gm: EstimateGrossMargin,
    category: MaterialSubCategory
  ): { year1: number; year2: number; year3: number; year4: number; year5: number } {
    switch (category) {
      case MaterialSubCategory.Siemens:
        return {
          year1: gm.siemensMaterialWarranty,
          year2: gm.siemensMaterialWarrantyForSecondYear,
          year3: gm.siemensMaterialWarrantyForThirdYear,
          year4: gm.siemensMaterialWarrantyForFourthYear,
          year5: gm.siemensMaterialWarrantyForFifthYear
        };
      case MaterialSubCategory.Outside:
        return {
          year1: gm.outsideMaterialWarranty,
          year2: gm.outsideMaterialWarrantyForSecondYear,
          year3: gm.outsideMaterialWarrantyForThirdYear,
          year4: gm.outsideMaterialWarrantyForFourthYear,
          year5: gm.outsideMaterialWarrantyForFifthYear
        };
      case MaterialSubCategory.Mechanical:
        return {
          year1: gm.machanicalMaterialWarranty,
          year2: gm.mechanicalMaterialWarrantyForSecondYear,
          year3: gm.mechanicalMaterialWarrantyForThirdYear,
          year4: gm.mechanicalMaterialWarrantyForFourthYear,
          year5: gm.mechanicalMaterialWarrantyForFifthYear
        };
      case MaterialSubCategory.Electrical:
        return {
          year1: gm.electricalMaterialWarranty,
          year2: gm.electricalMaterialWarrantyForSecondYear,
          year3: gm.electricalMaterialWarrantyForThirdYear,
          year4: gm.electricalMaterialWarrantyForFourthYear,
          year5: gm.electricalMaterialWarrantyForFifthYear
        };
      default:
        return { year1: CONSTANTS.ZERO, year2: CONSTANTS.ZERO, year3: CONSTANTS.ZERO, year4: CONSTANTS.ZERO, year5: CONSTANTS.ZERO };
    }
  }

  /**
   * Calculates the shipping cost for a single material category using GM data.
   * @param materialCost - The material cost for the category
   * @param gm - The EstimateGrossMargin data
   * @param category - Which material type ('siemens' | 'outside' | 'mechanical' | 'electrical')
   * @returns The calculated shipping cost
   */
  calculateShippingCostForCategory(
    materialCost: number,
    gm: EstimateGrossMargin,
    category: MaterialSubCategory
  ): number {
    const shippingRate = this.getShippingRateForCategory(gm, category);
    return this.calculateShippingCost(materialCost, shippingRate);
  }

  /**
   * Calculates the warranty cost for a single material category using GM data.
   * @param materialCost - The material cost for the category
   * @param gm - The EstimateGrossMargin data
   * @param category - Which material type ('siemens' | 'outside' | 'mechanical' | 'electrical')
   * @returns The calculated warranty cost
   */
  calculateWarrantyCostForCategory(
    materialCost: number,
    gm: EstimateGrossMargin,
    category: MaterialSubCategory
  ): number {
    const rates = this.getWarrantyRatesForCategory(gm, category);
    return this.calculateWarrantyTotal(materialCost, rates.year1, rates.year2, rates.year3, rates.year4, rates.year5);
  }

  /**
   * Returns the metadata actual (pre-promotion) material cost for a category.
   * Accepts either material category enum since both describe the same four buckets.
   */
  getActualMaterialCost(
    estimateTab: EstimateTab | undefined,
    category: MaterialSubCategory | SolMaterialEscalationType
  ): number | null {
    if (!estimateTab) {
      return null;
    }

    switch (category) {
      case MaterialSubCategory.Siemens:
      case SolMaterialEscalationType.Siemens_Material:
        return estimateTab.actualSiemensMaterialCost;
      case MaterialSubCategory.Outside:
      case SolMaterialEscalationType.Outside_Material:
        return estimateTab.actualOutsideMaterialCost;
      case MaterialSubCategory.Mechanical:
      case SolMaterialEscalationType.Mechanical_Material:
        return estimateTab.actualMechanicalMaterialCost;
      case MaterialSubCategory.Electrical:
      case SolMaterialEscalationType.Electrical_Material:
        return estimateTab.actualElectricalMaterialCost;
      default:
        return null;
    }
  }

  /**
   * Shipping & handling and warranty are always charged on the actual material cost - before any
   * product promotion discount and before any subsidy - escalated when escalation is enabled for
   * the category. Falls back to the supplied cost when no actual cost has been calculated yet.
   */
  getShippingWarrantyBaseCost(
    cost: number,
    category: MaterialSubCategory | SolMaterialEscalationType
  ): number {
    const estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab;
    return this.getEscalatedActualMaterialCost(estimateTab, category) ?? cost;
  }

  /**
   * Returns the metadata actual material cost for a category, escalated when
   * escalation is enabled for that category.
   */
  getEscalatedActualMaterialCost(
    estimateTab: EstimateTab | undefined,
    category: MaterialSubCategory | SolMaterialEscalationType
  ): number | null {
    const actualMaterialCost = this.getActualMaterialCost(estimateTab, category);
    if (actualMaterialCost === null) {
      return null;
    }

    const escalationData = this.getMaterialEscalationData(category);
    return escalationData?.escalationEnabled
      ? this.calculateTotalEscalatedCost({ ...escalationData, baseCost: actualMaterialCost })
      : actualMaterialCost;
  }

  private getMaterialEscalationData(
    category: MaterialSubCategory | SolMaterialEscalationType
  ): EscalationData | null {
    const escalationRates = this.commonDataService.getEstimateMetaData()?.escalationRates;
    switch (category) {
      case MaterialSubCategory.Siemens:
      case SolMaterialEscalationType.Siemens_Material:
        return escalationRates?.siemensMaterialEscalation;
      case MaterialSubCategory.Outside:
      case SolMaterialEscalationType.Outside_Material:
        return escalationRates?.outsideMaterialEscalation;
      case MaterialSubCategory.Mechanical:
      case SolMaterialEscalationType.Mechanical_Material:
        return escalationRates?.mechanicalMaterialEscalation;
      case MaterialSubCategory.Electrical:
      case SolMaterialEscalationType.Electrical_Material:
        return escalationRates?.electricalMaterialEscalation;
      default:
        return null;
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // ── Escalation ─────────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════

  /**
   * Calculates the total escalated cost for a single escalation category
   * using compound escalation rates.
   *
   * Formula per year (0-indexed):
   *   year 0: (baseCost / 100) × percentage_0
   *   year i (i ≥ 1): (baseCost / 100) × percentage_i × ∏(j=1..i) (1 + rate_j / 100)
   *
   * @param escalationData The escalation configuration containing percentages, rates, and base cost
   * @returns The total escalated cost across all 5 years
   */
  calculateTotalEscalatedCost(escalationData: EscalationData): number {
    const baseCost = escalationData.baseCost || CONSTANTS.ZERO;
    if (baseCost === CONSTANTS.ZERO) {
      return CONSTANTS.ZERO;
    }

    const percentages = [
      escalationData.firstYearPercentage || CONSTANTS.ZERO,
      escalationData.secondYearPercentage || CONSTANTS.ZERO,
      escalationData.thirdYearPercentage || CONSTANTS.ZERO,
      escalationData.fourthYearPercentage || CONSTANTS.ZERO,
      escalationData.fifthYearPercentage || CONSTANTS.ZERO
    ];

    const rates = [
      CONSTANTS.ZERO, // first year: no escalation rate applied
      escalationData.secondYearRate || CONSTANTS.ZERO,
      escalationData.thirdYearRate || CONSTANTS.ZERO,
      escalationData.fourthYearRate || CONSTANTS.ZERO,
      escalationData.fifthYearRate || CONSTANTS.ZERO
    ];

    let escalatedCost = CONSTANTS.ZERO;
    let compoundFactor = CONSTANTS.ONE;

    for (let i = 0; i < CONSTANTS.FIVE; i++) {
      if (i > 0) {
        compoundFactor *= (CONSTANTS.ONE + rates[i] / CONSTANTS.HUNDRED_POINT_ZERO);
      }
      escalatedCost += roundHalfEven9(
        (baseCost / CONSTANTS.HUNDRED_POINT_ZERO) * percentages[i] * compoundFactor
      );
    }

    return roundHalfEven9(escalatedCost);
  }

  calculateEscalationTotal(escalationRates: Escalations | undefined): number {
    if (!escalationRates) {
      return CONSTANTS.ZERO;
    }

    const categories = [
      escalationRates.siemensMaterialEscalation,
      escalationRates.outsideMaterialEscalation,
      escalationRates.mechanicalMaterialEscalation,
      escalationRates.electricalMaterialEscalation,
      escalationRates.pmLaborEscalation,
      escalationRates.engLaborEscalation,
      escalationRates.nengLaborEscalation,
      escalationRates.qengLaborEscalation,
      escalationRates.cengLaborEscalation,
      escalationRates.eengLaborEscalation,
      escalationRates.lengLaborEscalation,
      escalationRates.grapLaborEscalation,
      escalationRates.progLaborEscalation,
      escalationRates.specLaborEscalation,
      escalationRates.ddepLaborEscalation,
      escalationRates.mechLaborEscalation,
      escalationRates.elecLaborEscalation
    ];

    let escalatedCostTotal = CONSTANTS.ZERO;
    let baseCostTotal = CONSTANTS.ZERO;
    for (const category of categories) {
      if (category?.escalationEnabled) {
        escalatedCostTotal += category.escalatedCost || CONSTANTS.ZERO;
        baseCostTotal += category.baseCost || CONSTANTS.ZERO;
      }
    }

    // Escalated costs include the base costs; only the added cost is escalation.
    return escalatedCostTotal - baseCostTotal;
  }

  isMaterialEscalationEnabled(escalationRates: Escalations): boolean {
    return (
      escalationRates.siemensMaterialEscalation?.escalationEnabled ||
      escalationRates.siemensAtMaterialEscalation?.escalationEnabled ||
      escalationRates.outsideMaterialEscalation?.escalationEnabled ||
      escalationRates.mechanicalMaterialEscalation?.escalationEnabled ||
      escalationRates.electricalMaterialEscalation?.escalationEnabled
    );
  }

  isLaborEscalationEnabled(escalationRates: Escalations): boolean {
    const fixedLaborEnabled =
      escalationRates.pmLaborEscalation?.escalationEnabled ||
      escalationRates.elecLaborEscalation?.escalationEnabled ||
      escalationRates.specLaborEscalation?.escalationEnabled ||
      escalationRates.engLaborEscalation?.escalationEnabled ||
      escalationRates.nengLaborEscalation?.escalationEnabled ||
      escalationRates.qengLaborEscalation?.escalationEnabled ||
      escalationRates.cengLaborEscalation?.escalationEnabled ||
      escalationRates.eengLaborEscalation?.escalationEnabled ||
      escalationRates.lengLaborEscalation?.escalationEnabled ||
      escalationRates.mechLaborEscalation?.escalationEnabled ||
      escalationRates.progLaborEscalation?.escalationEnabled ||
      escalationRates.grapLaborEscalation?.escalationEnabled ||
      escalationRates.ddepLaborEscalation?.escalationEnabled;

    if (fixedLaborEnabled) {
      return true;
    }

    if (escalationRates.additionalLaborCatEscalationsList?.length > 0) {
      return escalationRates.additionalLaborCatEscalationsList.some(
        item => item.value?.escalationEnabled
      );
    }

    return false;
  }

  /**
   * Calculates the escalated cost for a single period using compound escalation rates.
   *
   * Formula:
   *   year 0: (baseCost / 100) × yearPercentage
   *   year i (i ≥ 1): (baseCost / 100) × yearPercentage × ∏(j=1..i) (1 + rates[j] / 100)
   *
   * @param baseCost The base cost to escalate
   * @param yearPercentage The distribution percentage for this specific year
   * @param rates Array of 5 rates: [0, secondYearRate, thirdYearRate, fourthYearRate, fifthYearRate]
   * @param yearIndex 0-based year index (0 = 1st year, 1 = 2nd year, etc.)
   * @returns The escalated cost for the specified period
   */
  calculateEscalatedCostForPeriod(
    baseCost: number,
    yearPercentage: number,
    rates: number[],
    yearIndex: number
  ): number {
    if (baseCost === CONSTANTS.ZERO || yearPercentage === CONSTANTS.ZERO) {
      return CONSTANTS.ZERO;
    }

    let compoundFactor = CONSTANTS.ONE;
    for (let i = 1; i <= yearIndex; i++) {
      compoundFactor *= (CONSTANTS.ONE + (rates[i] || CONSTANTS.ZERO) / CONSTANTS.HUNDRED_POINT_ZERO);
    }

    return roundHalfEven9(
      (baseCost / CONSTANTS.HUNDRED_POINT_ZERO) * yearPercentage * compoundFactor
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // ── Collaboration ─────────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════
  updateCollaborativeState(): boolean {
    return (this.commonDataService.isCollaborationEstimate()
      && this.commonDataService.getCollaborationState() !== CollaborationState.Edit);
  }
}